import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import styled from 'styled-components/native'
import moment from 'moment'

import { AvailabilitySlot } from '@dialogue/timekeeper'

import {
  selectLoadingAvailabilities,
  selectProviderPerDayAvailabilities,
  selectAvailabilityPerDateWithTime,
  selectSelectedProviderId,
  selectHasBookingPreferences,
  selectLoadingProviderAvailabilities,
} from 'APP/Store/ProviderBooking/selectors'
import { providerBookingActions } from 'APP/Store/ProviderBooking'
import { SelectedPreferences } from 'APP/Store/ProviderBooking/types'
import useShowBookingSheet from 'APP/Hooks/useShowBookingSheet'

import { InsuranceReimbursementNotice } from 'APP/Components/InsuranceReimbursementNotice'
import Typography from 'APP/Converse/Typography'
import { DayPicker } from './dayPicker'
import { NoAvailableSlots } from './noAvailableSlots'
import { ProviderSectionList } from './providerSectionList'
import { ProviderProfileModal } from './profile/ProviderProfileModal'
import { LoadingIndicator } from '../common/loadingIndicator'
import { TimeFrameMessage } from '../common/timeFrameMessage'
import type { CustomHeaderText } from '../AppointmentBookingBase/types'
import { FilterAvailabilities } from '../common/filterAvailabilities'
import { BookingBottomSheet, SheetType } from '../TimeBased/timeslotSelection'
import { transformAvailabilities } from 'APP/Store/AppointmentBooking/utils/availabilityMappers'
import { TransformStrategy } from 'APP/Components/AppointmentBooking/utils/types'
import { isOCAppointment } from 'APP/Lib/AppointmentManagementHelpers'

const Root = styled.View`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`

const ContentWrapper = styled.View`
  padding: ${({ theme }) => theme.metrics.baseMargin * 2}px
    ${({ theme }) => theme.metrics.baseMargin}px 0;
  gap: ${({ theme }) => theme.metrics.baseMargin}px;
`

const StyledTimeFrameMessage = styled(TimeFrameMessage)`
  text-align: left;
`

const StyledHeaderText = styled(Typography)`
  text-align: left;
`

interface Props {
  bookingId: string
  dateUntil: string
  dateFrom: string
  onConfirm: (slot: AvailabilitySlot | null) => boolean
  onQuit: () => void
  appointmentType: string
  confirmDisabled?: boolean
  customHeaderText?: CustomHeaderText
}

export const ProviderSelection = ({
  bookingId,
  dateFrom,
  dateUntil,
  onConfirm,
  onQuit,
  appointmentType,
  confirmDisabled = false,
  customHeaderText,
}: Props) => {
  const dispatch = useDispatch()
  const showSheet = useShowBookingSheet(appointmentType)
  const [isSheetVisible, setIsSheetVisible] = useState(false)
  const [activeTimeslot, setActiveTimeslot] = useState<AvailabilitySlot | null>(null)
  const isLoading = useSelector(selectLoadingAvailabilities)
  const stableAvailabilities = useSelector(selectProviderPerDayAvailabilities)
  const isLoadingProviderAvailabilities = useSelector(
    (state) =>
      !!activeTimeslot && selectLoadingProviderAvailabilities(state, activeTimeslot.provider_id)
  )

  const availabilitiesWithDateTime = useSelector(selectAvailabilityPerDateWithTime)
  const availabilitiesExists = useSelector((state) => state.appointmentBooking.availabilitiesExists)
  const selectedProviderId = useSelector(selectSelectedProviderId)
  const hasSelectedPreferences = useSelector(selectHasBookingPreferences)

  const availabilitiesPerPartOfDay = useMemo(
    () =>
      transformAvailabilities(stableAvailabilities, TransformStrategy.BY_PART_OF_DAY, {
        endAt: moment(dateUntil),
      }),
    [stableAvailabilities, dateUntil]
  )

  const handleOpenSheet = () => setIsSheetVisible(true)
  const handleCloseSheet = () => setIsSheetVisible(false)
  const handleSubmitSheet = () => {
    handleCloseSheet()
    onQuit()
  }

  const hasAvailabilities = stableAvailabilities?.length > 0
  const firstAvailability = stableAvailabilities?.[0]

  // Only the server can report whether the returned providers matched the preferences,
  // via provider_matching_attributes. When it is present with no matched_attributes,
  // the returned slots are fallbacks rather than preference matches.
  const matchingAttributes = firstAvailability?.provider_matching_attributes
  const noMatchedProviders = Boolean(
    matchingAttributes && !matchingAttributes.matched_attributes?.length
  )

  const timeFrameMessageKey =
    hasSelectedPreferences && noMatchedProviders ? 'no_matched_providers' : 'recommended_timeframe'

  const nextAvailability = availabilitiesWithDateTime?.[0]

  const handleSelectProvider = useCallback(
    (item: AvailabilitySlot) => {
      setActiveTimeslot(item)
      dispatch(
        providerBookingActions.getProviderAvailabilities({
          bookingId,
          providerId: item.provider_id,
          start_at: dateFrom,
          end_at: dateUntil,
        })
      )
    },
    [dispatch, bookingId, dateFrom, dateUntil]
  )

  const handleCloseModal = useCallback(() => {
    setActiveTimeslot(null)
    dispatch(providerBookingActions.setSelectedProviderId(null))
  }, [dispatch])

  const handleUpdatePreferences = useCallback(
    (preferences: SelectedPreferences) => {
      dispatch(providerBookingActions.updateBooking({ bookingId, preferences }))
    },
    [dispatch, bookingId]
  )

  return (
    <Root>
      <DayPicker
        dateFrom={dateFrom}
        dateUntil={dateUntil}
        availabilitiesExists={availabilitiesExists}
      />
      {!isLoading && (
        <ContentWrapper>
          {hasAvailabilities && (
            <>
              {customHeaderText ? (
                <StyledHeaderText variant="bodyNormal">
                  {customHeaderText({ dateFrom: moment(dateFrom), dateUntil: moment(dateUntil) })}
                </StyledHeaderText>
              ) : (
                <StyledTimeFrameMessage
                  dateFrom={moment(dateFrom)}
                  dateUntil={moment(dateUntil)}
                  messageKey={`AppointmentBooking.${timeFrameMessageKey}`}
                />
              )}
              {isOCAppointment(appointmentType) && <InsuranceReimbursementNotice />}
              <FilterAvailabilities onSubmit={handleUpdatePreferences} />
              <ProviderSectionList
                slots={availabilitiesPerPartOfDay}
                onSelectProvider={handleSelectProvider}
                onQuit={showSheet ? handleOpenSheet : onQuit}
                enableSchedulingLater={!isOCAppointment(appointmentType)}
              />
            </>
          )}
          {!hasAvailabilities && (
            <NoAvailableSlots
              hasNextAvailability
              onQuit={showSheet ? handleOpenSheet : onQuit}
              nextAvailability={nextAvailability}
            />
          )}
          <BookingBottomSheet
            isVisible={isSheetVisible}
            onCloseModal={handleCloseSheet}
            onConfirmCancel={handleSubmitSheet}
            sheetType={SheetType.AVAILABLE_APPOINTMENT}
          />
        </ContentWrapper>
      )}
      {isLoading && <LoadingIndicator />}
      <ProviderProfileModal
        selectedProviderId={selectedProviderId}
        baseChosenTimeslot={activeTimeslot}
        onConfirm={onConfirm}
        onClose={handleCloseModal}
        withAvailabilitySelection
        isLoadingSlots={isLoadingProviderAvailabilities}
        confirmDisabled={confirmDisabled}
      />
    </Root>
  )
}
